import type { Nullable } from "../types";
import type { AttendanceListItem } from "../types/attendances";

/**
 * Whether a recorded time entry may still be corrected, and by whom.
 *
 * An employee corrects their own entry while it is pending: booked time is a claim until somebody
 * signs it off, and afterwards it is the record the approval was given on. An administrator
 * corrects any entry in any state, because correcting the record is what administration is for.
 * An approver who wants an employee to fix their own entry resets it to pending, which hands it
 * back.
 *
 * Which of the two a caller is, is decided from their rules — an unrestricted `update Attendance`,
 * which is `manage all` — and not from this function, so the same answer holds on the server,
 * where it is enforced, and in the client, where it hides the controls.
 *
 * Break entries are filed approved by the break generator, so this rule leaves them to an
 * administrator without naming them.
 *
 * @param attendance - The attendance to evaluate.
 * @param role - `"admin"` for a caller whose `update Attendance` carries no condition; `"user"` for
 *   everybody else, whose right is bound to their own rows.
 * @returns `true` when the caller may correct the attendance.
 */
export function getIsAttendanceEditable(
	attendance: Nullable<Pick<AttendanceListItem, "state">>,
	role: "user" | "admin",
): boolean {
	if (!attendance) return false;
	if (role === "admin") return true;
	return attendance.state === "pending";
}
